import React, { useState, useMemo } from 'react';
import {
  X,
  Filter,
  AlertTriangle,
  CheckCircle2,
  Trash2,
  Clock,
  CreditCard,
  Bus,
  MapPin,
  ArrowRight,
  ShieldAlert,
  CheckSquare,
  Square,
  Sparkles,
  RefreshCw,
} from 'lucide-react';
import { Trip, University, Vehicle, TripStatus } from '../../../types';
import { tripService, BulkTripUpdateFields, isReturnTrip, isOutboundTrip } from '../../../services/tripService';
import { STANDARD_LAUNCH_STATIONS, DEFAULT_LAUNCH_STATION } from '../../../constants/tripRoutes';
import { getUniversityTheme, formatArabicFullDate } from '../AdminTrips';

interface BulkRouteTripsModalProps {
  isOpen: boolean;
  onClose: () => void;
  trips: Trip[];
  universities: University[];
  vehicles: Vehicle[];
  onSuccess: (message: string) => void;
  onRefresh: () => Promise<void>;
}

const WEEKDAYS = [
  { label: 'السبت', value: 6 },
  { label: 'الأحد', value: 0 },
  { label: 'الإثنين', value: 1 },
  { label: 'الثلاثاء', value: 2 },
  { label: 'الأربعاء', value: 3 },
  { label: 'الخميس', value: 4 },
  { label: 'الجمعة', value: 5 },
];

export const BulkRouteTripsModal: React.FC<BulkRouteTripsModalProps> = ({
  isOpen,
  onClose,
  trips,
  universities,
  vehicles,
  onSuccess,
  onRefresh,
}) => {
  // Wizard Steps: 'FILTER' -> 'ACTIONS' -> 'PLAN_PREVIEW'
  const [activeStep, setActiveStep] = useState<'FILTER' | 'ACTIONS' | 'PLAN_PREVIEW'>('FILTER');

  // --- Step 1: Filter / Target Scope ---
  const [selectedUniId, setSelectedUniId] = useState<string>('ALL');
  const [selectedDirection, setSelectedDirection] = useState<'ALL' | 'OUTBOUND' | 'RETURN'>('ALL');
  const [dateScope, setDateScope] = useState<'ALL_UPCOMING' | 'DATE_RANGE' | 'SPECIFIC_WEEKDAYS'>('ALL_UPCOMING');
  const [fromDate, setFromDate] = useState<string>(() => new Date().toISOString().split('T')[0]);
  const [toDate, setToDate] = useState<string>('');
  const [selectedWeekdays, setSelectedWeekdays] = useState<number[]>([0, 1, 2, 3, 4, 6]); // Default all student days
  const [selectedStation, setSelectedStation] = useState<string>('ALL');

  // --- Step 2: Action Specifications ---
  const [operationType, setOperationType] = useState<'UPDATE' | 'DELETE'>('UPDATE');

  // Pricing
  const [applyPrice, setApplyPrice] = useState<boolean>(false);
  const [priceMode, setPriceMode] = useState<'SET' | 'SUBTRACT' | 'ADD'>('SET');
  const [newPrice, setNewPrice] = useState<number>(120);
  const [priceDelta, setPriceDelta] = useState<number>(20);

  // Times
  const [applyTimes, setApplyTimes] = useState<boolean>(false);
  const [newDepTime, setNewDepTime] = useState<string>('06:30');
  const [newArrTime, setNewArrTime] = useState<string>('09:00');

  // Vehicle / Fleet & Capacity
  const [applyVehicle, setApplyVehicle] = useState<boolean>(false);
  const [fleetOption, setFleetOption] = useState<'HIACE_13' | 'MINIBUS_33' | 'COACH_50' | 'CUSTOM'>('HIACE_13');
  const [customVehicleId, setCustomVehicleId] = useState<string>(vehicles[0]?.id || '');

  // Launch Station
  const [applyStation, setApplyStation] = useState<boolean>(false);
  const [newLaunchStation, setNewLaunchStation] = useState<string>(DEFAULT_LAUNCH_STATION);

  // Status
  const [applyStatus, setApplyStatus] = useState<boolean>(false);
  const [newStatus, setNewStatus] = useState<TripStatus>('OPEN');

  // Delete Options
  const [skipTripsWithBookings, setSkipTripsWithBookings] = useState<boolean>(true);

  // --- Step 3: Plan Review State ---
  const [selectedTripIds, setSelectedTripIds] = useState<Set<string>>(new Set());
  const [isExecuting, setIsExecuting] = useState<boolean>(false);
  const [executionError, setExecutionError] = useState<string | null>(null);

  // 1. Calculate Matched Trips based on Step 1 Filters
  const matchedTrips = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];

    return trips.filter((t) => {
      // Exclude departed/completed from bulk operations unless explicitly included
      if (t.status === 'COMPLETED' || t.status === 'DEPARTED') return false;

      // University
      if (selectedUniId !== 'ALL' && t.universityId !== selectedUniId) {
        return false;
      }

      // Direction
      if (selectedDirection === 'OUTBOUND' && !isOutboundTrip(t.direction)) {
        return false;
      }
      if (selectedDirection === 'RETURN' && !isReturnTrip(t.direction)) {
        return false;
      }

      // Launch Station
      if (selectedStation !== 'ALL') {
        const tStation = t.launchStation || (t.origin?.includes('21') ? 'الكيلو 21' : 'الموقف الجديد بمحرم بيك');
        if (!tStation.includes(selectedStation)) {
          return false;
        }
      }

      // Date Scoping
      const tripDate = t.date || '';
      if (dateScope === 'ALL_UPCOMING') {
        if (tripDate < today) return false;
      } else if (dateScope === 'DATE_RANGE') {
        if (fromDate && tripDate < fromDate) return false;
        if (toDate && tripDate > toDate) return false;
      } else if (dateScope === 'SPECIFIC_WEEKDAYS') {
        if (tripDate < today) return false;
        try {
          const parts = tripDate.split('-');
          const dt = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
          const weekday = dt.getDay();
          if (!selectedWeekdays.includes(weekday)) return false;
        } catch {
          return false;
        }
      }

      return true;
    }).sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  }, [trips, selectedUniId, selectedDirection, selectedStation, dateScope, fromDate, toDate, selectedWeekdays]);

  // Total booked passengers in matched trips
  const totalBookedPassengers = useMemo(() => {
    return matchedTrips.reduce((acc, t) => {
      const booked = Math.max(0, (t.totalSeats || 50) - (t.availableSeats !== undefined ? t.availableSeats : 50));
      return acc + booked;
    }, 0);
  }, [matchedTrips]);

  // Trips that have active bookings
  const tripsWithBookingsCount = useMemo(() => {
    return matchedTrips.filter((t) => {
      const booked = Math.max(0, (t.totalSeats || 50) - (t.availableSeats !== undefined ? t.availableSeats : 50));
      return booked > 0;
    }).length;
  }, [matchedTrips]);

  // Handler: Move to Step 3 (Plan Preview)
  const handleGeneratePlan = () => {
    if (matchedTrips.length === 0) {
      alert('لا توجد أي رحلات مطابقة للفلاتر المحددة. يرجى تعديل خيارات الفلترة أولاً.');
      return;
    }

    if (operationType === 'UPDATE') {
      const hasAnyUpdateSelected = applyPrice || applyTimes || applyVehicle || applyStation || applyStatus;
      if (!hasAnyUpdateSelected) {
        alert('يرجى تفعيل خيار واحد على الأقل للتعديل (مثل السعر أو المواعيد أو المركبة أو المحطة أو الحالة).');
        return;
      }
    }

    // Default: select all matched trips
    setSelectedTripIds(new Set(matchedTrips.map((t) => t.id)));
    setExecutionError(null);
    setActiveStep('PLAN_PREVIEW');
  };

  // Toggle single trip inclusion in plan
  const handleToggleTripInPlan = (tripId: string) => {
    setSelectedTripIds((prev) => {
      const next = new Set(prev);
      if (next.has(tripId)) {
        next.delete(tripId);
      } else {
        next.add(tripId);
      }
      return next;
    });
  };

  // Select / Deselect all
  const handleSelectAllInPlan = () => {
    if (selectedTripIds.size === matchedTrips.length) {
      setSelectedTripIds(new Set());
    } else {
      setSelectedTripIds(new Set(matchedTrips.map((t) => t.id)));
    }
  };

  // Compute what each trip will look like in the plan
  const computedPlanTrips = useMemo(() => {
    return matchedTrips.map((trip) => {
      const isSelected = selectedTripIds.has(trip.id);
      const bookedCount = Math.max(0, (trip.totalSeats || 50) - (trip.availableSeats !== undefined ? trip.availableSeats : 50));

      // 1. Price
      let plannedPrice = trip.price || 150;
      if (operationType === 'UPDATE' && applyPrice) {
        if (priceMode === 'SET' && newPrice > 0) {
          plannedPrice = newPrice;
        } else if (priceMode === 'SUBTRACT') {
          plannedPrice = Math.max(1, (trip.price || 150) - priceDelta);
        } else if (priceMode === 'ADD') {
          plannedPrice = (trip.price || 150) + priceDelta;
        }
      }

      // 2. Times
      const plannedDepTime = operationType === 'UPDATE' && applyTimes && newDepTime ? newDepTime : trip.departureTime;
      const plannedArrTime = operationType === 'UPDATE' && applyTimes && newArrTime ? newArrTime : trip.expectedArrivalTime || trip.arrivalTime || '09:00';

      // 3. Vehicle & Capacity
      let plannedCapacity = trip.totalSeats || 50;
      let plannedVehicleName = trip.vehiclePlate || trip.vehicleNumber || 'حافلة سياحية';
      if (operationType === 'UPDATE' && applyVehicle) {
        if (fleetOption === 'HIACE_13') {
          plannedCapacity = 13;
          plannedVehicleName = 'تويوتا هاي إس 13 راكب (موديل 2026)';
        } else if (fleetOption === 'MINIBUS_33') {
          plannedCapacity = 33;
          plannedVehicleName = 'ميني باص سياحي 33 راكب';
        } else if (fleetOption === 'COACH_50') {
          plannedCapacity = 50;
          plannedVehicleName = 'حافلة سياحية كبرى 50 راكب';
        } else {
          const v = vehicles.find((item) => item.id === customVehicleId);
          if (v) {
            plannedCapacity = v.capacity || 50;
            plannedVehicleName = `${v.model || v.vehicleNumber} (${plannedCapacity} راكب)`;
          }
        }
      }

      // 4. Station
      const plannedStation = operationType === 'UPDATE' && applyStation && newLaunchStation ? newLaunchStation : (trip.launchStation || 'الموقف الجديد بمحرم بيك');

      // 5. Status
      const plannedStatus = operationType === 'UPDATE' && applyStatus ? newStatus : trip.status;

      return {
        trip,
        isSelected,
        bookedCount,
        hasBookings: bookedCount > 0,
        current: {
          price: trip.price || 150,
          depTime: trip.departureTime,
          arrTime: trip.expectedArrivalTime || trip.arrivalTime || '09:00',
          capacity: trip.totalSeats || 50,
          vehicleName: trip.vehiclePlate || trip.vehicleNumber || 'حافلة سياحية',
          station: trip.launchStation || 'الموقف الجديد بمحرم بيك',
          status: trip.status,
        },
        planned: {
          price: plannedPrice,
          depTime: plannedDepTime,
          arrTime: plannedArrTime,
          capacity: plannedCapacity,
          vehicleName: plannedVehicleName,
          station: plannedStation,
          status: plannedStatus,
        },
      };
    });
  }, [
    matchedTrips,
    selectedTripIds,
    operationType,
    applyPrice,
    priceMode,
    newPrice,
    priceDelta,
    applyTimes,
    newDepTime,
    newArrTime,
    applyVehicle,
    fleetOption,
    customVehicleId,
    vehicles,
    applyStation,
    newLaunchStation,
    applyStatus,
    newStatus,
  ]);

  // Execute the Plan
  const handleExecutePlan = async () => {
    const tripsToProcess = matchedTrips.filter((t) => selectedTripIds.has(t.id));
    if (tripsToProcess.length === 0) {
      alert('يرجى تحديد رحلة واحدة على الأقل من جدول الخطة لتطبيق الإجراء عليها.');
      return;
    }

    if (operationType === 'DELETE') {
      const confirmMsg = `تحذير هام: هل أنت متأكد من حذف ${tripsToProcess.length} رحلة نهائياً من قاعدة البيانات؟ لا يمكن التراجع عن هذه الخطوة.`;
      if (!window.confirm(confirmMsg)) return;
    }

    setIsExecuting(true);
    setExecutionError(null);

    try {
      if (operationType === 'DELETE') {
        const res = await tripService.bulkDeleteTrips(tripsToProcess, {
          skipTripsWithBookings,
        });

        if (!res.success && res.errors.length > 0) {
          setExecutionError(res.errors.join(' | '));
        } else {
          onSuccess(`تم بنجاح حذف ${res.deletedCount || 0} رحلة${res.skippedCount > 0 ? ` (تم استثناء ${res.skippedCount} رحلة لوجود حجوزات طلاب نشطة بها)` : ''}.`);
          await onRefresh();
          onClose();
        }
      } else {
        // Prepare updates
        const updates: BulkTripUpdateFields = {};

        if (applyPrice) {
          updates.priceMode = priceMode;
          if (priceMode === 'SET') {
            updates.price = newPrice;
          } else {
            updates.priceDelta = priceDelta;
          }
        }

        if (applyTimes) {
          updates.departureTime = newDepTime;
          updates.expectedArrivalTime = newArrTime;
        }

        if (applyStation) {
          updates.launchStation = newLaunchStation;
        }

        if (applyVehicle) {
          if (fleetOption === 'HIACE_13') {
            updates.vehiclePlate = 'تويوتا هاي إس 2026';
            updates.vehicleNumber = 'تويوتا هاي إس 2026';
            updates.totalSeats = 13;
          } else if (fleetOption === 'MINIBUS_33') {
            updates.vehiclePlate = 'ميني باص سياحي 33 راكب';
            updates.vehicleNumber = 'ميني باص سياحي 33 راكب';
            updates.totalSeats = 33;
          } else if (fleetOption === 'COACH_50') {
            updates.vehiclePlate = 'حافلة سياحية كبرى 50 راكب';
            updates.vehicleNumber = 'حافلة سياحية كبرى 50 راكب';
            updates.totalSeats = 50;
          } else {
            const v = vehicles.find((item) => item.id === customVehicleId);
            if (v) {
              updates.vehicleId = v.id;
              updates.vehiclePlate = v.vehicleNumber || v.plateNumber || 'حافلة سياحية';
              updates.vehicleNumber = v.vehicleNumber || v.plateNumber || 'حافلة سياحية';
              updates.totalSeats = v.capacity || 50;
            }
          }
        }

        if (applyStatus) {
          updates.status = newStatus;
        }

        const res = await tripService.bulkUpdateTrips(tripsToProcess, updates);

        if (!res.success && res.errors.length > 0) {
          setExecutionError(res.errors.join(' | '));
        } else {
          onSuccess(`تم بنجاح تحديث وتطبيق الخطة على ${res.updatedCount} رحلة لخط السير المحدد!`);
          await onRefresh();
          onClose();
        }
      }
    } catch (err: any) {
      setExecutionError(err?.message || 'حدث خطأ غير متوقع أثناء معالجة الخطة.');
    } finally {
      setIsExecuting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="admin-modal-overlay" onClick={onClose} dir="rtl">
      <div
        className="admin-modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '860px', width: '95vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}
      >
        {/* Modal Header */}
        <div className="admin-modal-header" style={{ padding: '14px 20px', borderBottom: '1px solid #E2E8F0' }}>
          <div className="flex items-center gap-3">
            <div
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
                color: '#1D4ED8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(37, 99, 235, 0.15)',
              }}
            >
              <Sparkles size={22} />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <span>التعديل والإدارة الجماعية لرحلات خط السير</span>
                <span className="text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded-full font-mono">
                  Bulk Route Manager
                </span>
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                تعديل الأسعار، المواعيد، الأسطول، أو حذف الرحلات دفعة واحدة مع توليد الخطة أولاً للمعاينة والاعتماد
              </p>
            </div>
          </div>
          <button type="button" className="admin-modal-close-btn" onClick={onClose} disabled={isExecuting}>
            <X size={20} />
          </button>
        </div>

        {/* Stepper Wizard Bar */}
        <div
          style={{
            background: '#F8FAFC',
            padding: '10px 20px',
            borderBottom: '1px solid #E2E8F0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
          }}
        >
          {/* Step 1 Tab */}
          <button
            type="button"
            onClick={() => setActiveStep('FILTER')}
            disabled={isExecuting}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              borderRadius: '8px',
              border: activeStep === 'FILTER' ? '1.5px solid #2563EB' : '1px solid #E2E8F0',
              background: activeStep === 'FILTER' ? '#EFF6FF' : '#FFFFFF',
              color: activeStep === 'FILTER' ? '#1D4ED8' : '#64748B',
              fontWeight: activeStep === 'FILTER' ? 'bold' : 'normal',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            <div
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: activeStep === 'FILTER' ? '#2563EB' : '#E2E8F0',
                color: activeStep === 'FILTER' ? '#FFFFFF' : '#64748B',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold',
              }}
            >
              1
            </div>
            <span style={{ fontSize: '13px' }}>1. تحديد خط السير والنطاق ({matchedTrips.length})</span>
          </button>

          <ArrowRight size={16} style={{ color: '#94A3B8' }} />

          {/* Step 2 Tab */}
          <button
            type="button"
            onClick={() => setActiveStep('ACTIONS')}
            disabled={isExecuting || matchedTrips.length === 0}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              borderRadius: '8px',
              border: activeStep === 'ACTIONS' ? '1.5px solid #2563EB' : '1px solid #E2E8F0',
              background: activeStep === 'ACTIONS' ? '#EFF6FF' : '#FFFFFF',
              color: activeStep === 'ACTIONS' ? '#1D4ED8' : '#64748B',
              fontWeight: activeStep === 'ACTIONS' ? 'bold' : 'normal',
              cursor: matchedTrips.length === 0 ? 'not-allowed' : 'pointer',
              opacity: matchedTrips.length === 0 ? 0.6 : 1,
              transition: 'all 0.2s',
            }}
          >
            <div
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: activeStep === 'ACTIONS' ? '#2563EB' : '#E2E8F0',
                color: activeStep === 'ACTIONS' ? '#FFFFFF' : '#64748B',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold',
              }}
            >
              2
            </div>
            <span style={{ fontSize: '13px' }}>2. تحديد التعديلات والعمليات</span>
          </button>

          <ArrowRight size={16} style={{ color: '#94A3B8' }} />

          {/* Step 3 Tab */}
          <button
            type="button"
            onClick={handleGeneratePlan}
            disabled={isExecuting || matchedTrips.length === 0}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              borderRadius: '8px',
              border: activeStep === 'PLAN_PREVIEW' ? '1.5px solid #10B981' : '1px solid #E2E8F0',
              background: activeStep === 'PLAN_PREVIEW' ? '#ECFDF5' : '#FFFFFF',
              color: activeStep === 'PLAN_PREVIEW' ? '#047857' : '#64748B',
              fontWeight: activeStep === 'PLAN_PREVIEW' ? 'bold' : 'normal',
              cursor: matchedTrips.length === 0 ? 'not-allowed' : 'pointer',
              opacity: matchedTrips.length === 0 ? 0.6 : 1,
              transition: 'all 0.2s',
            }}
          >
            <div
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: activeStep === 'PLAN_PREVIEW' ? '#10B981' : '#E2E8F0',
                color: activeStep === 'PLAN_PREVIEW' ? '#FFFFFF' : '#64748B',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold',
              }}
            >
              3
            </div>
            <span style={{ fontSize: '13px' }}>3. خطة التعديل والمعاينة 🛡️</span>
          </button>
        </div>

        {/* Modal Body */}
        <div className="admin-modal-body" style={{ flex: 1, overflowY: 'auto', padding: '18px 22px' }}>
          {/* ================= STEP 1: FILTER / TARGET SCOPE ================= */}
          {activeStep === 'FILTER' && (
            <div className="space-y-4">
              <div
                style={{
                  background: '#F1F5F9',
                  border: '1px solid #CBD5E1',
                  borderRadius: '10px',
                  padding: '12px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                    <Filter size={16} className="text-blue-600" />
                    <span>تحديد نطاق الرحلات وخط السير المستهدف</span>
                  </h4>
                  <p className="text-xs text-slate-500 mt-0.5">
                    حدد الجامعة، نوع الرحلات (ذهاب أو عودة)، ونطاق المواعيد لاستهداف الرحلات المطلوبة
                  </p>
                </div>
                <div className="text-left bg-white px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm">
                  <span className="text-xs text-slate-500 block">الرحلات المطابقة</span>
                  <span className="font-mono text-base font-bold text-blue-700">{matchedTrips.length} رحلة</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* 1. Target University */}
                <div>
                  <label className="admin-form-label font-bold text-xs text-slate-700 block mb-1">
                    الجامعة المستهدفة
                  </label>
                  <select
                    className="admin-form-input w-full"
                    value={selectedUniId}
                    onChange={(e) => setSelectedUniId(e.target.value)}
                  >
                    <option value="ALL">🌐 كافة الجامعات المجدولة (تعديل عام)</option>
                    {universities.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.governorate})
                      </option>
                    ))}
                  </select>
                </div>

                {/* 2. Direction */}
                <div>
                  <label className="admin-form-label font-bold text-xs text-slate-700 block mb-1">
                    نوع واتجاه الرحلات
                  </label>
                  <select
                    className="admin-form-input w-full"
                    value={selectedDirection}
                    onChange={(e) => setSelectedDirection(e.target.value as any)}
                  >
                    <option value="ALL">🔄 رحلات الذهاب والعودة معاً</option>
                    <option value="OUTBOUND">➡ رحلات الذهاب فقط (من الإسكندرية إلى الجامعة)</option>
                    <option value="RETURN">⬅ رحلات العودة فقط (من الجامعة إلى الإسكندرية)</option>
                  </select>
                </div>

                {/* 3. Station */}
                <div>
                  <label className="admin-form-label font-bold text-xs text-slate-700 block mb-1">
                    محطة الانطلاق / التجمع (اختياري)
                  </label>
                  <select
                    className="admin-form-input w-full"
                    value={selectedStation}
                    onChange={(e) => setSelectedStation(e.target.value)}
                  >
                    <option value="ALL">كافة المحطات ومناطق التجمع</option>
                    {STANDARD_LAUNCH_STATIONS.map((st) => (
                      <option key={st} value={st}>
                        {st}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 4. Date Scope Type */}
                <div>
                  <label className="admin-form-label font-bold text-xs text-slate-700 block mb-1">
                    نطاق التواريخ والأيام
                  </label>
                  <select
                    className="admin-form-input w-full"
                    value={dateScope}
                    onChange={(e) => setDateScope(e.target.value as any)}
                  >
                    <option value="ALL_UPCOMING">📅 كافة الرحلات القادمة المجدولة من اليوم فصاعداً</option>
                    <option value="DATE_RANGE">📆 نطاق تواريخ محدد (من تاريخ إلى تاريخ)</option>
                    <option value="SPECIFIC_WEEKDAYS">🗓️ أيام محددة من الأسبوع (مثل: كل أيام الأحد)</option>
                  </select>
                </div>
              </div>

              {/* Conditional Date Range inputs */}
              {dateScope === 'DATE_RANGE' && (
                <div className="grid grid-cols-2 gap-3 p-3 bg-blue-50/50 rounded-xl border border-blue-100">
                  <div>
                    <label className="text-xs font-bold text-slate-600 block mb-1">من تاريخ:</label>
                    <input
                      type="date"
                      className="admin-form-input w-full"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-600 block mb-1">إلى تاريخ (اختياري):</label>
                    <input
                      type="date"
                      className="admin-form-input w-full"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* Conditional Weekdays selector */}
              {dateScope === 'SPECIFIC_WEEKDAYS' && (
                <div className="p-3 bg-blue-50/50 rounded-xl border border-blue-100">
                  <label className="text-xs font-bold text-slate-700 block mb-2">
                    حدد أيام الأسبوع لتطبيق التعديل على رحلاتها فقط:
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {WEEKDAYS.map((w) => {
                      const isChecked = selectedWeekdays.includes(w.value);
                      return (
                        <button
                          key={w.value}
                          type="button"
                          onClick={() => {
                            setSelectedWeekdays((prev) =>
                              prev.includes(w.value)
                                ? prev.filter((d) => d !== w.value)
                                : [...prev, w.value]
                            );
                          }}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '8px',
                            border: isChecked ? '1.5px solid #2563EB' : '1px solid #CBD5E1',
                            background: isChecked ? '#EFF6FF' : '#FFFFFF',
                            color: isChecked ? '#1D4ED8' : '#64748B',
                            fontWeight: isChecked ? 'bold' : 'normal',
                            fontSize: '12px',
                            cursor: 'pointer',
                          }}
                        >
                          {isChecked ? '✓ ' : ''}
                          {w.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Matched Summary Banner */}
              <div
                style={{
                  background: matchedTrips.length > 0 ? '#F0FDF4' : '#FEF2F2',
                  border: matchedTrips.length > 0 ? '1px solid #BBF7D0' : '1px solid #FECACA',
                  borderRadius: '10px',
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div className="flex items-center gap-2">
                  {matchedTrips.length > 0 ? (
                    <CheckCircle2 size={18} className="text-emerald-600" />
                  ) : (
                    <AlertTriangle size={18} className="text-red-500" />
                  )}
                  <span className="text-xs font-bold" style={{ color: matchedTrips.length > 0 ? '#166534' : '#991B1B' }}>
                    {matchedTrips.length > 0
                      ? `تم العثور على ${matchedTrips.length} رحلة مطابقة لنطاق الفلترة.`
                      : 'لا توجد رحلات تطابق هذا الفلتر حالياً.'}
                  </span>
                </div>
                {totalBookedPassengers > 0 && (
                  <span className="text-xs bg-amber-100 text-amber-800 border border-amber-200 px-2.5 py-0.5 rounded-full font-bold">
                    ⚠️ بها {totalBookedPassengers} طالب حاجز مقاعد
                  </span>
                )}
              </div>
            </div>
          )}

          {/* ================= STEP 2: ACTIONS SPECIFICATION ================= */}
          {activeStep === 'ACTIONS' && (
            <div className="space-y-4">
              {/* Operation Mode Selector: Update vs Delete */}
              <div className="flex gap-2 p-1 bg-slate-100 rounded-xl border border-slate-200">
                <button
                  type="button"
                  onClick={() => setOperationType('UPDATE')}
                  style={{
                    flex: 1,
                    padding: '8px 14px',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    fontSize: '13px',
                    background: operationType === 'UPDATE' ? '#FFFFFF' : 'transparent',
                    color: operationType === 'UPDATE' ? '#1E293B' : '#64748B',
                    boxShadow: operationType === 'UPDATE' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}
                >
                  <Sparkles size={16} className="text-blue-600" />
                  <span>تعديل بيانات الرحلات (الأسعار / المواعيد / الأسطول)</span>
                </button>

                <button
                  type="button"
                  onClick={() => setOperationType('DELETE')}
                  style={{
                    flex: 1,
                    padding: '8px 14px',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    fontSize: '13px',
                    background: operationType === 'DELETE' ? '#FEE2E2' : 'transparent',
                    color: operationType === 'DELETE' ? '#DC2626' : '#64748B',
                    boxShadow: operationType === 'DELETE' ? '0 1px 3px rgba(220,38,38,0.15)' : 'none',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}
                >
                  <Trash2 size={16} />
                  <span>حذف جماعي للرحلات المحددة</span>
                </button>
              </div>

              {/* UPDATE ACTIONS BLOCK */}
              {operationType === 'UPDATE' && (
                <div className="space-y-3">
                  <p className="text-xs text-slate-500 mb-2">
                    قم بتفعيل خيار أو أكثر من التعديلات التالية لتطبيقها دفعة واحدة على الرحلات المختارة ({matchedTrips.length} رحلة):
                  </p>

                  {/* 1. Price Modification */}
                  <div
                    style={{
                      border: applyPrice ? '1.5px solid #3B82F6' : '1px solid #E2E8F0',
                      borderRadius: '10px',
                      padding: '12px 14px',
                      background: applyPrice ? '#F0F9FF' : '#FFFFFF',
                      transition: 'all 0.2s',
                    }}
                  >
                    <label className="flex items-center gap-2 cursor-pointer font-bold text-xs text-slate-800">
                      <input
                        type="checkbox"
                        checked={applyPrice}
                        onChange={(e) => setApplyPrice(e.target.checked)}
                        className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                      />
                      <CreditCard size={16} className="text-blue-600" />
                      <span>تعديل سعر التذكرة (Price Update)</span>
                    </label>

                    {applyPrice && (
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 pr-6">
                        <div>
                          <label className="text-xs text-slate-600 block mb-1">طريقة التسعير:</label>
                          <select
                            className="admin-form-input w-full text-xs"
                            value={priceMode}
                            onChange={(e) => setPriceMode(e.target.value as any)}
                          >
                            <option value="SET">تعيين سعر موحد جديد (مثلاً 120 ج.م)</option>
                            <option value="SUBTRACT">تخفيض وخصم مبلغ محدد (مثلاً -20 ج.م)</option>
                            <option value="ADD">زيادة مبلغ محدد على السعر الحالي (+15 ج.م)</option>
                          </select>
                        </div>

                        <div>
                          <label className="text-xs text-slate-600 block mb-1">
                            {priceMode === 'SET' ? 'السعر الجديد الموحد (ج.م):' : 'المبلغ المراد خصمه أو زيادته (ج.م):'}
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="1000"
                            className="admin-form-input w-full font-mono font-bold"
                            value={priceMode === 'SET' ? newPrice : priceDelta}
                            onChange={(e) => {
                              const val = Number(e.target.value);
                              if (priceMode === 'SET') setNewPrice(val);
                              else setPriceDelta(val);
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 2. Times Modification */}
                  <div
                    style={{
                      border: applyTimes ? '1.5px solid #3B82F6' : '1px solid #E2E8F0',
                      borderRadius: '10px',
                      padding: '12px 14px',
                      background: applyTimes ? '#F0F9FF' : '#FFFFFF',
                      transition: 'all 0.2s',
                    }}
                  >
                    <label className="flex items-center gap-2 cursor-pointer font-bold text-xs text-slate-800">
                      <input
                        type="checkbox"
                        checked={applyTimes}
                        onChange={(e) => setApplyTimes(e.target.checked)}
                        className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                      />
                      <Clock size={16} className="text-blue-600" />
                      <span>تعديل موعد التحرك والوصول (Departure & Arrival Times)</span>
                    </label>

                    {applyTimes && (
                      <div className="mt-3 grid grid-cols-2 gap-3 pr-6">
                        <div>
                          <label className="text-xs text-slate-600 block mb-1">وقت التحرك والانطلاق:</label>
                          <input
                            type="time"
                            className="admin-form-input w-full font-mono text-center"
                            value={newDepTime}
                            onChange={(e) => setNewDepTime(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-600 block mb-1">وقت الوصول المتوقع:</label>
                          <input
                            type="time"
                            className="admin-form-input w-full font-mono text-center"
                            value={newArrTime}
                            onChange={(e) => setNewArrTime(e.target.value)}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 3. Vehicle / Fleet & Capacity Modification */}
                  <div
                    style={{
                      border: applyVehicle ? '1.5px solid #3B82F6' : '1px solid #E2E8F0',
                      borderRadius: '10px',
                      padding: '12px 14px',
                      background: applyVehicle ? '#F0F9FF' : '#FFFFFF',
                      transition: 'all 0.2s',
                    }}
                  >
                    <label className="flex items-center gap-2 cursor-pointer font-bold text-xs text-slate-800">
                      <input
                        type="checkbox"
                        checked={applyVehicle}
                        onChange={(e) => setApplyVehicle(e.target.checked)}
                        className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                      />
                      <Bus size={16} className="text-blue-600" />
                      <span>تغيير نوع المركبة / الأسطول وسعة المقاعد (Fleet & Capacity)</span>
                    </label>

                    {applyVehicle && (
                      <div className="mt-3 pr-6 space-y-2">
                        <label className="text-xs text-slate-600 block mb-1">اختر فئة المركبة وسعة المقاعد:</label>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                          <button
                            type="button"
                            onClick={() => setFleetOption('HIACE_13')}
                            style={{
                              padding: '8px 10px',
                              borderRadius: '8px',
                              border: fleetOption === 'HIACE_13' ? '2px solid #2563EB' : '1px solid #CBD5E1',
                              background: fleetOption === 'HIACE_13' ? '#EFF6FF' : '#FFFFFF',
                              textAlign: 'right',
                              cursor: 'pointer',
                            }}
                          >
                            <span className="font-bold text-xs block text-slate-800">تويوتا هاي إس</span>
                            <span className="text-xs text-slate-500 font-mono">13 راكباً (موديل 2026)</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => setFleetOption('MINIBUS_33')}
                            style={{
                              padding: '8px 10px',
                              borderRadius: '8px',
                              border: fleetOption === 'MINIBUS_33' ? '2px solid #2563EB' : '1px solid #CBD5E1',
                              background: fleetOption === 'MINIBUS_33' ? '#EFF6FF' : '#FFFFFF',
                              textAlign: 'right',
                              cursor: 'pointer',
                            }}
                          >
                            <span className="font-bold text-xs block text-slate-800">ميني باص سياحي</span>
                            <span className="text-xs text-slate-500 font-mono">33 راكباً (كوستر)</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => setFleetOption('COACH_50')}
                            style={{
                              padding: '8px 10px',
                              borderRadius: '8px',
                              border: fleetOption === 'COACH_50' ? '2px solid #2563EB' : '1px solid #CBD5E1',
                              background: fleetOption === 'COACH_50' ? '#EFF6FF' : '#FFFFFF',
                              textAlign: 'right',
                              cursor: 'pointer',
                            }}
                          >
                            <span className="font-bold text-xs block text-slate-800">حافلة سياحية كبرى</span>
                            <span className="text-xs text-slate-500 font-mono">50 راكباً</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => setFleetOption('CUSTOM')}
                            style={{
                              padding: '8px 10px',
                              borderRadius: '8px',
                              border: fleetOption === 'CUSTOM' ? '2px solid #2563EB' : '1px solid #CBD5E1',
                              background: fleetOption === 'CUSTOM' ? '#EFF6FF' : '#FFFFFF',
                              textAlign: 'right',
                              cursor: 'pointer',
                            }}
                          >
                            <span className="font-bold text-xs block text-slate-800">مركبة محددة من الأسطول</span>
                            <span className="text-xs text-slate-500 font-mono">تحديد بالحافلة ورقم اللوحة</span>
                          </button>
                        </div>

                        {fleetOption === 'CUSTOM' && (
                          <div className="mt-2.5">
                            <label className="text-xs text-slate-600 block mb-1">اختر المركبة من أسطول الحافلات المسجل:</label>
                            <select
                              className="admin-form-input w-full"
                              value={customVehicleId}
                              onChange={(e) => setCustomVehicleId(e.target.value)}
                            >
                              {vehicles.map((v) => (
                                <option key={v.id} value={v.id}>
                                  {v.vehicleNumber} ({v.model || 'حافلة'}) - سعة {v.capacity} راكب
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        <p className="text-xs text-slate-500">
                          ✨ عند تغيير سعة المركبة، يقوم النظام تلقائياً بتحديث السعة وحساب المقاعد المتاحة مع الحفاظ على الحجوزات الموجودة.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* 4. Launch Station Modification */}
                  <div
                    style={{
                      border: applyStation ? '1.5px solid #3B82F6' : '1px solid #E2E8F0',
                      borderRadius: '10px',
                      padding: '12px 14px',
                      background: applyStation ? '#F0F9FF' : '#FFFFFF',
                      transition: 'all 0.2s',
                    }}
                  >
                    <label className="flex items-center gap-2 cursor-pointer font-bold text-xs text-slate-800">
                      <input
                        type="checkbox"
                        checked={applyStation}
                        onChange={(e) => setApplyStation(e.target.checked)}
                        className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                      />
                      <MapPin size={16} className="text-blue-600" />
                      <span>تعديل محطة الانطلاق / نقطة التجمع</span>
                    </label>

                    {applyStation && (
                      <div className="mt-3 pr-6">
                        <select
                          className="admin-form-input w-full"
                          value={newLaunchStation}
                          onChange={(e) => setNewLaunchStation(e.target.value)}
                        >
                          {STANDARD_LAUNCH_STATIONS.map((st) => (
                            <option key={st} value={st}>
                              {st}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {/* 5. Status Modification */}
                  <div
                    style={{
                      border: applyStatus ? '1.5px solid #3B82F6' : '1px solid #E2E8F0',
                      borderRadius: '10px',
                      padding: '12px 14px',
                      background: applyStatus ? '#F0F9FF' : '#FFFFFF',
                      transition: 'all 0.2s',
                    }}
                  >
                    <label className="flex items-center gap-2 cursor-pointer font-bold text-xs text-slate-800">
                      <input
                        type="checkbox"
                        checked={applyStatus}
                        onChange={(e) => setApplyStatus(e.target.checked)}
                        className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4"
                      />
                      <CheckCircle2 size={16} className="text-blue-600" />
                      <span>تغيير حالة الرحلات (فتح / إغلاق الحجز)</span>
                    </label>

                    {applyStatus && (
                      <div className="mt-3 pr-6">
                        <select
                          className="admin-form-input w-full"
                          value={newStatus}
                          onChange={(e) => setNewStatus(e.target.value as any)}
                        >
                          <option value="OPEN">مفتوحة للحجز (OPEN)</option>
                          <option value="FULL">مكتملة / مغلقة مؤقتاً (FULL)</option>
                          <option value="CANCELLED">ملغاة (CANCELLED)</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* DELETE ACTIONS BLOCK */}
              {operationType === 'DELETE' && (
                <div className="p-4 bg-red-50 rounded-xl border border-red-200 space-y-3">
                  <div className="flex items-start gap-3">
                    <ShieldAlert size={24} className="text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-bold text-red-900">
                        حذف جماعي للرحلات المطابقة ({matchedTrips.length} رحلة)
                      </h4>
                      <p className="text-xs text-red-700 mt-1 leading-relaxed">
                        سيتم حذف كافة الرحلات المحددة نهائياً من قاعدة البيانات. لتفادي أي خلل في حجوزات الطلاب، يمكنك تفعيل خيار الحماية أدناه:
                      </p>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-red-200">
                    <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-slate-800">
                      <input
                        type="checkbox"
                        checked={skipTripsWithBookings}
                        onChange={(e) => setSkipTripsWithBookings(e.target.checked)}
                        className="rounded text-red-600 focus:ring-red-500 w-4 h-4"
                      />
                      <span>حماية الطلاب: استثناء وتخطي أي رحلة تحتوي على مقاعد محجوزة بالفعل وعدم حذفها</span>
                    </label>
                    <p className="text-xs text-slate-500 mr-6 mt-0.5">
                      يضمن عدم إلغاء أي مقعد أو رحلة قام طالب بحجزها وسداد قيمتها.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ================= STEP 3: PLAN PREVIEW & EXECUTION ================= */}
          {activeStep === 'PLAN_PREVIEW' && (
            <div className="space-y-4">
              {/* Plan Overview Banner */}
              <div
                style={{
                  background: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)',
                  border: '1px solid #86EFAC',
                  borderRadius: '12px',
                  padding: '14px 18px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <h4 className="text-sm font-bold text-emerald-950 flex items-center gap-2">
                    <Sparkles size={18} className="text-emerald-600" />
                    <span>خطة التعديل الجماعي المقترحة (Preview Plan)</span>
                  </h4>
                  <p className="text-xs text-emerald-800 mt-0.5">
                    راجع جدول المقارنة التالي للتأكد من التغييرات قبل الاعتماد. يمكنك إلغاء تحديد أي رحلة لاستثنائها.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <div className="bg-white/90 backdrop-blur px-3 py-1.5 rounded-lg border border-emerald-200 text-center">
                    <span className="text-[11px] text-slate-500 block">الرحلات بالخطة</span>
                    <strong className="text-sm font-mono text-emerald-800">
                      {selectedTripIds.size} / {matchedTrips.length}
                    </strong>
                  </div>
                  <div className="bg-white/90 backdrop-blur px-3 py-1.5 rounded-lg border border-emerald-200 text-center">
                    <span className="text-[11px] text-slate-500 block">الطلاب المسجلين</span>
                    <strong className="text-sm font-mono text-emerald-800">{totalBookedPassengers} طالب</strong>
                  </div>
                </div>
              </div>

              {/* Warning if trips have active bookings */}
              {tripsWithBookingsCount > 0 && (
                <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 flex items-center gap-2.5 text-xs text-amber-900">
                  <AlertTriangle size={18} className="text-amber-600 flex-shrink-0" />
                  <span>
                    <strong>تنبيه:</strong> هناك {tripsWithBookingsCount} رحلة تحتوي على حجوزات قائمة للطلاب ({totalBookedPassengers} راكب).
                    {operationType === 'UPDATE'
                      ? ' سيتم الحفاظ التام على أرقام مقاعد الطلاب المسجلين ولن يتأثر حجزهم.'
                      : skipTripsWithBookings
                      ? ' تم تفعيل خيار الحماية وسيتم استثناء هذه الرحلات من الحذف تلقائياً.'
                      : ' تنبيه: خيار الحماية معطل وسيتم حذف هذه الرحلات بما فيها من حجوزات!'}
                  </span>
                </div>
              )}

              {/* Error banner if any */}
              {executionError && (
                <div className="p-3 bg-red-50 text-red-800 rounded-xl border border-red-200 text-xs">
                  ❌ {executionError}
                </div>
              )}

              {/* Table Action Bar */}
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={handleSelectAllInPlan}
                  className="text-xs font-bold text-blue-700 hover:text-blue-900 flex items-center gap-1.5"
                >
                  {selectedTripIds.size === matchedTrips.length ? (
                    <>
                      <Square size={14} />
                      <span>إلغاء تحديد الكل</span>
                    </>
                  ) : (
                    <>
                      <CheckSquare size={14} />
                      <span>تحديد كافة الرحلات ({matchedTrips.length})</span>
                    </>
                  )}
                </button>

                <span className="text-xs text-slate-500">
                  تم تحديد <strong>{selectedTripIds.size}</strong> رحلة للاعتماد
                </span>
              </div>

              {/* Diff Preview Table */}
              <div
                style={{
                  maxHeight: '320px',
                  overflowY: 'auto',
                  border: '1px solid #E2E8F0',
                  borderRadius: '10px',
                  background: '#FFFFFF',
                }}
              >
                <table className="w-full text-right text-xs" style={{ borderCollapse: 'collapse' }}>
                  <thead style={{ position: 'sticky', top: 0, background: '#F8FAFC', borderBottom: '1px solid #CBD5E1', zIndex: 2 }}>
                    <tr>
                      <th style={{ padding: '8px 10px', width: '36px' }}>#</th>
                      <th style={{ padding: '8px 10px' }}>الرحلة واليوم</th>
                      <th style={{ padding: '8px 10px' }}>الجامعة والاتجاه</th>
                      <th style={{ padding: '8px 10px' }}>الحجوزات</th>
                      {operationType === 'UPDATE' ? (
                        <>
                          {applyPrice && <th style={{ padding: '8px 10px' }}>السعر (قبل ⬅ بعد)</th>}
                          {applyTimes && <th style={{ padding: '8px 10px' }}>الوقت (قبل ⬅ بعد)</th>}
                          {applyVehicle && <th style={{ padding: '8px 10px' }}>السعة (قبل ⬅ بعد)</th>}
                          {applyStation && <th style={{ padding: '8px 10px' }}>المحطة الجديدة</th>}
                          {applyStatus && <th style={{ padding: '8px 10px' }}>الحالة</th>}
                        </>
                      ) : (
                        <th style={{ padding: '8px 10px', color: '#DC2626' }}>الإجراء المقرر</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {computedPlanTrips.map((row, idx) => {
                      const uniTheme = getUniversityTheme(row.trip.universityName);
                      return (
                        <tr
                          key={row.trip.id}
                          style={{
                            borderBottom: '1px solid #F1F5F9',
                            background: row.isSelected ? (idx % 2 === 0 ? '#FFFFFF' : '#FAFAFA') : '#F8FAFC',
                            opacity: row.isSelected ? 1 : 0.45,
                          }}
                        >
                          {/* Checkbox */}
                          <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={row.isSelected}
                              onChange={() => handleToggleTripInPlan(row.trip.id)}
                              className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer"
                            />
                          </td>

                          {/* Code & Date */}
                          <td style={{ padding: '8px 10px' }}>
                            <span className="font-bold text-slate-800 block">{formatArabicFullDate(row.trip.date)}</span>
                            <span className="font-mono text-[11px] text-slate-400">
                              {row.trip.tripCode || row.trip.id.substring(0, 8)}
                            </span>
                          </td>

                          {/* University & Direction */}
                          <td style={{ padding: '8px 10px' }}>
                            <span
                              className="inline-block px-2 py-0.5 rounded text-[11px] font-bold"
                              style={{
                                background: uniTheme.badgeBg,
                                color: uniTheme.badgeText,
                                border: `1px solid ${uniTheme.badgeBorder}`,
                              }}
                            >
                              {row.trip.universityName}
                            </span>
                            <span
                              className="block text-[10px] text-slate-500 mt-0.5"
                              style={{ color: isReturnTrip(row.trip.direction) ? '#7C3AED' : '#0369A1' }}
                            >
                              {isReturnTrip(row.trip.direction) ? '⬅ عودة للإسكندرية' : '➡ ذهاب للجامعة'}
                            </span>
                          </td>

                          {/* Bookings */}
                          <td style={{ padding: '8px 10px' }}>
                            <span
                              className={`font-mono font-bold px-2 py-0.5 rounded text-xs ${
                                row.hasBookings ? 'bg-amber-100 text-amber-900 border border-amber-200' : 'bg-slate-100 text-slate-600'
                              }`}
                            >
                              {row.bookedCount} / {row.current.capacity}
                            </span>
                          </td>

                          {/* Diff columns for UPDATE */}
                          {operationType === 'UPDATE' ? (
                            <>
                              {applyPrice && (
                                <td style={{ padding: '8px 10px' }}>
                                  <div className="flex items-center gap-1 font-mono">
                                    <span className="line-through text-slate-400">{row.current.price}</span>
                                    <ArrowRight size={12} className="text-slate-400" />
                                    <strong className="text-emerald-700 font-bold">{row.planned.price} ج.م</strong>
                                  </div>
                                </td>
                              )}

                              {applyTimes && (
                                <td style={{ padding: '8px 10px' }}>
                                  <div className="flex items-center gap-1 font-mono">
                                    <span className="line-through text-slate-400">{row.current.depTime}</span>
                                    <ArrowRight size={12} className="text-slate-400" />
                                    <strong className="text-blue-700 font-bold">{row.planned.depTime}</strong>
                                  </div>
                                </td>
                              )}

                              {applyVehicle && (
                                <td style={{ padding: '8px 10px' }}>
                                  <div className="flex items-center gap-1 font-mono">
                                    <span className="line-through text-slate-400">{row.current.capacity}</span>
                                    <ArrowRight size={12} className="text-slate-400" />
                                    <strong className="text-indigo-700 font-bold">{row.planned.capacity} مقعد</strong>
                                  </div>
                                </td>
                              )}

                              {applyStation && (
                                <td style={{ padding: '8px 10px' }}>
                                  <span className="text-[11px] font-bold text-slate-700">{row.planned.station}</span>
                                </td>
                              )}

                              {applyStatus && (
                                <td style={{ padding: '8px 10px' }}>
                                  <span
                                    className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                      row.planned.status === 'OPEN'
                                        ? 'bg-emerald-100 text-emerald-800'
                                        : row.planned.status === 'FULL'
                                        ? 'bg-red-100 text-red-800'
                                        : 'bg-slate-200 text-slate-700'
                                    }`}
                                  >
                                    {row.planned.status === 'OPEN' ? 'مفتوحة' : row.planned.status === 'FULL' ? 'مكتملة' : 'ملغاة'}
                                  </span>
                                </td>
                              )}
                            </>
                          ) : (
                            <td style={{ padding: '8px 10px' }}>
                              <span className="font-bold text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded text-[11px]">
                                {row.hasBookings && skipTripsWithBookings ? '🛡️ سيتم تخطيها (بها طلاب)' : '🗑️ حذف نهائي'}
                              </span>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div
          className="admin-modal-footer"
          style={{
            padding: '12px 20px',
            borderTop: '1px solid #E2E8F0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {/* Left / Back Button */}
          <div>
            {activeStep === 'FILTER' && (
              <button type="button" className="btn-admin-outline" onClick={onClose} disabled={isExecuting}>
                إلغاء
              </button>
            )}
            {activeStep === 'ACTIONS' && (
              <button
                type="button"
                className="btn-admin-outline"
                onClick={() => setActiveStep('FILTER')}
                disabled={isExecuting}
              >
                العودة لتحديد النطاق
              </button>
            )}
            {activeStep === 'PLAN_PREVIEW' && (
              <button
                type="button"
                className="btn-admin-outline"
                onClick={() => setActiveStep('ACTIONS')}
                disabled={isExecuting}
              >
                تعديل خيارات الخطة
              </button>
            )}
          </div>

          {/* Right / Next & Execute Button */}
          <div className="flex items-center gap-2">
            {activeStep === 'FILTER' && (
              <button
                type="button"
                className="btn-admin-primary"
                onClick={() => setActiveStep('ACTIONS')}
                disabled={matchedTrips.length === 0}
              >
                <span>متابعة لتحديد التعديلات ({matchedTrips.length} رحلة)</span>
                <ArrowRight size={16} />
              </button>
            )}

            {activeStep === 'ACTIONS' && (
              <button
                type="button"
                className="btn-admin-primary"
                style={{ background: '#059669', borderColor: '#059669' }}
                onClick={handleGeneratePlan}
              >
                <Sparkles size={16} />
                <span>معاينة وتوليد الخطة أولاً 🛡️ ({matchedTrips.length} رحلة)</span>
              </button>
            )}

            {activeStep === 'PLAN_PREVIEW' && (
              <button
                type="button"
                className={operationType === 'DELETE' ? 'btn-admin-danger' : 'btn-admin-primary'}
                style={
                  operationType === 'UPDATE'
                    ? { background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', borderColor: '#059669' }
                    : {}
                }
                onClick={handleExecutePlan}
                disabled={isExecuting || selectedTripIds.size === 0}
              >
                {isExecuting ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    <span>جاري تطبيق الخطة في قاعدة البيانات...</span>
                  </>
                ) : operationType === 'DELETE' ? (
                  <>
                    <Trash2 size={16} />
                    <span>تأكيد وحذف ({selectedTripIds.size}) رحلة نهائياً</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={16} />
                    <span>🚀 اعتماد وتطبيق الخطة على ({selectedTripIds.size}) رحلة</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
